import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listPublicPlans } from '@/services/plans';
import type { ProductPlan } from '@/domain/types';

const faqs = [
  { q: 'Is this a one-time payment or subscription?', a: 'It depends on the plan. One-time plans give you lifetime access with updates for the duration shown. Subscription plans include ongoing updates and support for as long as you are subscribed.' },
  { q: 'Can I upgrade my license later?', a: 'Yes. Contact support to upgrade from a lower to a higher tier. We will prorate the difference so you only pay for what you need.' },
  { q: 'What happens if my subscription expires?', a: 'The plugin continues to work after expiry. You lose access to automatic updates and support until you renew. A 7-day grace period is included before features are gated.' },
  { q: 'Do you offer refunds?', a: 'Yes, we offer a 14-day money-back guarantee on all products. If you are not satisfied, contact support for a full refund — no questions asked.' },
  { q: 'How does license activation work?', a: 'After purchase you receive a unique license key. Enter it in your WordPress admin under Settings to activate. You can manage and deactivate sites from your account dashboard.' },
  { q: 'Can I use one license on multiple sites?', a: 'Site limits depend on the plan you choose. Agency and higher plans cover multiple sites. Each plan shows its site limit clearly before you purchase.' },
];

function CardSkeleton({ popular = false }: { popular?: boolean }) {
  return (
    <div className={`rounded-2xl p-6 md:p-8 border animate-pulse ${popular ? 'bg-surface-800 border-surface-700 scale-[1.02]' : 'bg-white border-surface-200'}`}>
      <div className={`h-3 rounded w-1/3 mb-2 ${popular ? 'bg-surface-700' : 'bg-surface-200'}`} />
      <div className={`h-6 rounded w-1/2 mb-4 ${popular ? 'bg-surface-700' : 'bg-surface-200'}`} />
      <div className={`h-10 rounded w-2/5 mb-6 ${popular ? 'bg-surface-700' : 'bg-surface-200'}`} />
      {[1,2,3,4].map(i => <div key={i} className={`h-3 rounded mb-3 ${popular ? 'bg-surface-700' : 'bg-surface-200'}`} style={{ width: `${55 + i * 10}%` }} />)}
      <div className={`h-10 rounded-lg mt-6 ${popular ? 'bg-surface-700' : 'bg-surface-200'}`} />
    </div>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<ProductPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    listPublicPlans().then(setPlans).catch(() => setPlans([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-surface-50 pt-20 md:pt-24 pb-16">
      <div className="px-4 md:px-6 max-w-6xl mx-auto">

        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-2xl md:text-4xl font-bold text-surface-900 mb-3">Simple, Transparent Pricing</h1>
          <p className="text-surface-500 max-w-lg mx-auto text-sm md:text-base">No hidden fees. Pick the plan that fits your workflow.</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center mb-16">
            <CardSkeleton /><CardSkeleton popular /><CardSkeleton />
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-16 text-surface-400 text-sm mb-16">No plans available yet.</div>
        ) : (
          <div className={`grid gap-6 md:gap-8 items-center mb-16 ${plans.length === 1 ? 'max-w-sm mx-auto' : plans.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto' : 'grid-cols-1 md:grid-cols-3'}`}>
            {plans.slice(0, 3).map((plan) => {
              const sitesLabel = plan.siteLimit >= 999 ? 'Unlimited sites' : `${plan.siteLimit} site${plan.siteLimit !== 1 ? 's' : ''}`;
              return (
                <div key={plan.id} className={`relative rounded-2xl p-6 md:p-8 border transition-all duration-300 hover:shadow-lg flex flex-col ${plan.isPopular ? 'bg-surface-900 text-white border-surface-900 scale-[1.02] md:scale-[1.04]' : 'bg-white border-surface-200 hover:border-brand-300'}`}>
                  {plan.isPopular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-brand-500 text-white text-xs font-semibold whitespace-nowrap">Most Popular</span>}
                  {plan.description && <p className={`text-xs font-medium mb-1 ${plan.isPopular ? 'text-surface-400' : 'text-surface-500'}`}>{plan.description}</p>}
                  <h3 className={`text-lg font-semibold mb-1 ${plan.isPopular ? 'text-white' : 'text-surface-900'}`}>{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-5">
                    <span className={`text-3xl md:text-4xl font-bold ${plan.isPopular ? 'text-white' : 'text-surface-900'}`}>${plan.price}</span>
                    <span className={`text-sm ${plan.isPopular ? 'text-surface-400' : 'text-surface-500'}`}>{plan.billingType === 'one_time' ? 'one-time' : `/${plan.intervalUnit}`}</span>
                  </div>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    <li className="flex items-start gap-2 text-sm"><i className={`ri-check-line mt-0.5 shrink-0 ${plan.isPopular ? 'text-brand-400' : 'text-brand-600'}`} /><span className={plan.isPopular ? 'text-surface-300' : 'text-surface-600'}>{sitesLabel}</span></li>
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm"><i className={`ri-check-line mt-0.5 shrink-0 ${plan.isPopular ? 'text-brand-400' : 'text-brand-600'}`} /><span className={plan.isPopular ? 'text-surface-300' : 'text-surface-600'}>{f}</span></li>
                    ))}
                  </ul>
                  <button onClick={() => navigate('/products')} className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${plan.isPopular ? 'bg-brand-500 hover:bg-brand-400 text-white' : 'bg-surface-100 hover:bg-surface-200 text-surface-900'}`}>Get Started</button>
                </div>
              );
            })}
          </div>
        )}

        {/* Guarantee */}
        <div className="bg-brand-50 border border-brand-100 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row items-center gap-4 mb-16 text-center sm:text-left">
          <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center shrink-0 mx-auto sm:mx-0"><i className="ri-shield-check-line text-xl text-brand-600" /></div>
          <div className="flex-1">
            <h3 className="font-bold text-surface-900 text-base mb-1">14-Day Money-Back Guarantee</h3>
            <p className="text-sm text-surface-500">Not satisfied? Get a full refund within 14 days — no questions asked.</p>
          </div>
          <button onClick={() => navigate('/products')} className="shrink-0 px-5 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors whitespace-nowrap">Browse Products</button>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-surface-900 text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-surface-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left gap-4">
                  <span className="font-medium text-surface-900 text-sm">{faq.q}</span>
                  <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-surface-500 shrink-0`} />
                </button>
                {openFaq === idx && <div className="px-5 pb-4 text-sm text-surface-500 leading-relaxed border-t border-surface-100 pt-3">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
